import { NgStyle } from '@angular/common';
import { Directive, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { mapStyles } from './token-mapping';
import { PStyle } from '../../core/types';

/**
 * Angular directive for applying design system tokens to CSS properties.
 *
 * This directive combines multiple token types (colors, typography, sizes, etc.)
 * into a single style binding. Each token category has its own mapper that converts
 * token names to CSS variables.
 *
 * @selector `[pStyle]`
 * @standalone
 *
 * @example
 * ```html
 * <div [pStyle]="{
 *     color: {
 *         color: disabled ? null : color,
 *         'background-color': 'functional-default'
 *     },
 *     typography: {
 *         'font-size': 'heading-1',
 *         'font-weight': 'heading-1',
 *         'line-height': 'heading-1'
 *     },
 *     size: {
 *         width: 'lg',
 *         height: 'lg'
 *     },
 *     spacing: {
 *         padding: 'md',
 *         gap: 'sm'
 *     },
 *     radius: {
 *         'border-radius': 'md'
 *     },
 *     component: {
 *         width: ['action-button', 'size', 'lg'],
 *         height: ['action-button', 'size', 'lg'],
 *         'stroke-width': ['icon', 'stroke', 'sm']
 *     },
 *     pseudo: {
 *         hover: {
 *             color: { color: 'logo-light' },
 *             typography: { 'font-size': 'heading-2' }
 *         },
 *         focus: {
 *             color: { color: 'primary' }
 *         }
 *     }
 * }"></div>
 * ```
 */
@Directive({
    selector: '[pStyle]',
    standalone: true,
    hostDirectives: [NgStyle]
})
export class StyleDirective implements OnChanges {
    @Input() public pStyle: PStyle = {};

    private readonly ngStyle = inject(NgStyle);

    ngOnChanges(changes: SimpleChanges): void {
        const pStyle = changes['pStyle']?.currentValue as PStyle;

        this.ngStyle.ngStyle = pStyle ? mapStyles(pStyle) : undefined;
        this.ngStyle.ngDoCheck();
    }
}
